import React from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Ensure autoTable is available on jsPDF prototype
if (typeof jsPDF.prototype.autoTable === 'undefined') {
  jsPDF.prototype.autoTable = autoTable;
}

/**
 * ExportPDF Component
 * A reusable component for exporting structured table data to PDF format
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of objects containing the data to export
 * @param {Array} props.columns - Array of column definitions with key and label properties
 * @param {string} props.title - Title to display at the top of the PDF
 * @param {string} props.filename - Filename for the downloaded PDF (without .pdf extension)
 * @param {Array} props.visibleColumns - Optional array of column keys to include (if not provided, all columns are included)
 * @param {Object} props.options - Additional PDF generation options
 * @param {boolean} props.options.includeTimestamp - Whether to include timestamp (default: true)
 * @param {string} props.options.pageSize - PDF page size (default: 'a4')
 * @param {string} props.options.orientation - PDF orientation (default: 'portrait')
 * @param {Object} props.options.styles - Custom styling options
 * @param {Function} props.onSuccess - Callback function called on successful export
 * @param {Function} props.onError - Callback function called on export error
 * @param {boolean} props.disabled - Whether the export is disabled
 * @param {string} props.buttonText - Custom text for the export button
 * @param {string} props.buttonClassName - Custom CSS class for the button
 */
const ExportPDF = ({
  data = [],
  columns = [],
  title = 'Data Export',
  filename = 'export',
  visibleColumns = null,
  options = {},
  onSuccess = () => {},
  onError = () => {},
  disabled = false,
  buttonText = 'Export PDF',
  buttonClassName = ''
}) => {
  // Default options
  const defaultOptions = {
    includeTimestamp: true,
    pageSize: 'a4',
    orientation: 'portrait',
    styles: {
      fontSize: 9,
      cellPadding: 3,
      headFillColor: [43, 70, 99],
      headTextColor: 255,
      headFontStyle: 'bold',
      alternateRowFillColor: [248, 249, 250],
      margin: { top: 40 }
    }
  };

  const finalOptions = { ...defaultOptions, ...options };

  // Validate dependencies
  const checkDependencies = () => {
    if (typeof jsPDF === 'undefined') {
      throw new Error('jsPDF is not available. Please ensure jspdf is installed.');
    }
    if (typeof jsPDF.prototype.autoTable === 'undefined') {
      throw new Error('jspdf-autotable plugin is not available. Please ensure jspdf-autotable is installed.');
    }
  };

  // Validate input data
  const validateInput = () => {
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('Data must be a non-empty array.');
    }
    if (!Array.isArray(columns) || columns.length === 0) {
      throw new Error('Columns must be a non-empty array.');
    }
    if (!title || typeof title !== 'string') {
      throw new Error('Title must be a non-empty string.');
    }
    if (!filename || typeof filename !== 'string') {
      throw new Error('Filename must be a non-empty string.');
    }
  };

  // Filter columns to only include visible ones if specified
  const getFilteredColumns = () => {
    if (!visibleColumns) return columns;
    
    return columns.filter(col => 
      visibleColumns.includes(col.key) || visibleColumns.includes(col.label)
    );
  };

  // Prepare table data - export exactly what's provided without modification
  const prepareTableData = (filteredColumns) => {
    return data.map(row => 
      filteredColumns.map(col => {
        const value = row[col.key];
        
        // Handle different data types without corruption
        if (value === null || value === undefined) {
          return ''; // Empty string for null/undefined values
        }
        
        if (Array.isArray(value)) {
          return value.join(', '); // Join arrays with comma and space
        }
        
        if (typeof value === 'object') {
          try {
            return JSON.stringify(value); // Convert objects to JSON string
          } catch (error) {
            return String(value); // Fallback to string conversion
          }
        }
        
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No'; // Convert booleans to readable text
        }
        
        if (typeof value === 'number') {
          return value.toString(); // Convert numbers to string
        }
        
        return String(value); // Convert everything else to string
      })
    );
  };

  // Generate and download PDF
  const generatePDF = async () => {
    try {
      // Validate inputs and dependencies
      validateInput();
      checkDependencies();

      // Create PDF document
      const doc = new jsPDF(finalOptions.orientation, 'pt', finalOptions.pageSize);
      
      // Filter columns
      const filteredColumns = getFilteredColumns();
      
      // Prepare table data
      const tableData = prepareTableData(filteredColumns);
      
      // Add title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(title, 14, 22);
      
      // Add timestamp if enabled
      if (finalOptions.includeTimestamp) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 32);
      }
      
      // Create table
      doc.autoTable({
        head: [filteredColumns.map(col => col.label)],
        body: tableData,
        startY: finalOptions.includeTimestamp ? 40 : 30,
        styles: {
          fontSize: finalOptions.styles.fontSize,
          cellPadding: finalOptions.styles.cellPadding,
        },
        headStyles: {
          fillColor: finalOptions.styles.headFillColor,
          textColor: finalOptions.styles.headTextColor,
          fontStyle: finalOptions.styles.headFontStyle,
        },
        alternateRowStyles: {
          fillColor: finalOptions.styles.alternateRowFillColor,
        },
        margin: finalOptions.styles.margin,
        didDrawPage: (data) => {
          // Add page numbers
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.text(`Page ${data.pageNumber} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10);
        }
      });
      
      // Save the PDF
      const finalFilename = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
      doc.save(finalFilename);
      
      // Call success callback
      onSuccess({
        format: 'PDF',
        filename: finalFilename,
        dataCount: data.length,
        columnCount: filteredColumns.length
      });
      
    } catch (error) {
      console.error('PDF export failed:', error);
      
      // Call error callback
      onError({
        format: 'PDF',
        error: error.message,
        details: error
      });
      
      // Show user-friendly error message
      alert(`PDF export failed: ${error.message}`);
    }
  };

  // Handle button click
  const handleExport = (event) => {
    event.preventDefault();
    if (!disabled) {
      generatePDF();
    }
  };

  return (
    <button
      className={`export-pdf-button ${buttonClassName}`}
      onClick={handleExport}
      disabled={disabled}
      style={{
        background: 'linear-gradient(135deg, #f7fbfc 0%, #edf6f8 100%)',
        color: '#134b5f',
        border: '1.5px solid #7aa7b6',
        borderRadius: '14px',
        padding: '10px 16px',
        fontWeight: 600,
        fontSize: 14,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        boxShadow: '0 10px 24px rgba(19, 75, 95, 0.12)',
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.target.style.transform = 'translateY(-1px)';
          e.target.style.background = 'linear-gradient(135deg, #e4f4f7 0%, #ffffff 100%)';
          e.target.style.boxShadow = '0 14px 28px rgba(19, 75, 95, 0.16)';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.target.style.transform = 'translateY(0)';
          e.target.style.background = 'linear-gradient(135deg, #f7fbfc 0%, #edf6f8 100%)';
          e.target.style.boxShadow = '0 10px 24px rgba(19, 75, 95, 0.12)';
        }
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/>
        <polyline points="14,2 14,8 20,8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10,9 9,9 8,9"/>
      </svg>
      {buttonText}
    </button>
  );
};

export default ExportPDF;
